import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { LoaderCircle, Sparkles, Trash2 } from 'lucide-react'
import { api } from '@/lib/api'
import type {
  AdminJobSummary,
  Category,
  DiscoverSection,
  MoodPreset,
  Page,
  StylePreset,
  Wallpaper,
} from '@/lib/types'
import { PageHeader } from '@/components/AppLayout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input, Textarea } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { formatDate, formatElapsed } from '@/lib/utils'
import { ErrorNote } from './DashboardPage'

/**
 * Discover administration.
 *
 * Curated wallpapers are generated once here and reused across every user's
 * feed, which is what keeps browsing free of per-view model cost. Note there is
 * no way to promote a user's private wallpaper into Discover — publishing
 * someone's work is a privacy decision the product has not asked them to make.
 */
export function DiscoverPage() {
  const queryClient = useQueryClient()

  const sections = useQuery({
    queryKey: ['discover', 'sections'],
    queryFn: () => api.get<DiscoverSection[]>('api/admin/discover/sections'),
  })

  const wallpapers = useQuery({
    queryKey: ['discover', 'wallpapers'],
    queryFn: () => api.get<Page<Wallpaper>>('api/admin/discover/wallpapers', { size: 48 }),
  })

  const categories = useQuery({
    queryKey: ['catalog', 'categories'],
    queryFn: () => api.get<Category[]>('api/admin/catalog/categories'),
  })

  const styles = useQuery({
    queryKey: ['catalog', 'styles'],
    queryFn: () => api.get<StylePreset[]>('api/admin/catalog/styles'),
  })

  const moods = useQuery({
    queryKey: ['catalog', 'moods'],
    queryFn: () => api.get<MoodPreset[]>('api/admin/catalog/moods'),
  })

  const retire = useMutation({
    mutationFn: (id: string) => api.delete(`api/admin/discover/wallpapers/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['discover', 'wallpapers'] }),
  })

  const generate = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<Wallpaper>('api/admin/generations/curated', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['discover', 'wallpapers'] }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['jobs'] }),
  })

  // Curated jobs are the owner-less ones. Poll faster while this tab is waiting
  // on its own generation so the new job shows up within a few seconds.
  const running = useQuery({
    queryKey: ['jobs', 'running-curated'],
    queryFn: () => api.get<Page<AdminJobSummary>>('api/admin/generations', { status: 'RUNNING', size: 25 }),
    select: (page) => page.items.filter((job) => job.userId === null),
    refetchInterval: generate.isPending ? 3_000 : 10_000,
  })
  const runningCurated = running.data ?? []

  const error = sections.error ?? wallpapers.error ?? retire.error ?? generate.error

  return (
    <>
      <PageHeader
        title="Discover"
        description="Curated wallpapers and the sections that show them."
        actions={
          <GenerateCuratedDialog
            categories={categories.data ?? []}
            styles={styles.data ?? []}
            moods={moods.data ?? []}
            pending={generate.isPending}
            onSubmit={(body) => generate.mutate(body)}
          />
        }
      />

      <div className="space-y-6 p-8">
        {error && <ErrorNote message={(error as Error).message} />}
        {runningCurated.length > 0 ? (
          <RunningCuratedJobs jobs={runningCurated} />
        ) : (
          generate.isPending && (
            <p className="text-sm text-[var(--color-text-secondary)]">
              Starting generation — this can take a minute or two.
            </p>
          )
        )}

        <Card>
          <CardHeader>
            <CardTitle>Sections</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {sections.data?.map((section) => (
              <Badge key={section.id} variant={section.active ? 'accent' : 'default'}>
                {section.nameEn} · {section.kind}
              </Badge>
            ))}
          </CardContent>
        </Card>

        <div>
          <h2 className="mb-3 text-sm font-medium text-[var(--color-text-secondary)]">
            Curated wallpapers ({wallpapers.data?.totalItems ?? 0})
          </h2>

          {wallpapers.data?.items.length === 0 && (
            <p className="rounded-[var(--radius-card)] border bg-[var(--color-surface)] p-8 text-center text-sm text-[var(--color-text-tertiary)]">
              No curated wallpapers yet. Generate one to populate Discover.
            </p>
          )}

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6">
            {wallpapers.data?.items.map((wallpaper) => (
              <div key={wallpaper.id} className="group relative">
                <div className="aspect-[9/19.5] overflow-hidden rounded-[var(--radius-card)] border bg-[var(--color-surface-elevated)]">
                  {wallpaper.previewUrl ? (
                    <img
                      src={wallpaper.previewUrl}
                      alt={wallpaper.title ?? 'Curated wallpaper'}
                      className="size-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex size-full items-center justify-center text-xs text-[var(--color-text-tertiary)]">
                      No preview
                    </div>
                  )}
                </div>
                <p className="mt-2 truncate text-xs text-[var(--color-text-secondary)]">
                  {wallpaper.title ?? '—'}
                </p>
                <p className="text-xs text-[var(--color-text-tertiary)]">{formatDate(wallpaper.createdAt)}</p>
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute right-2 top-2 opacity-0 transition-opacity group-hover:opacity-100"
                  aria-label="Retire wallpaper"
                  disabled={retire.isPending}
                  onClick={() => retire.mutate(wallpaper.id)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}

function RunningCuratedJobs({ jobs }: { jobs: AdminJobSummary[] }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000)
    return () => window.clearInterval(timer)
  }, [])

  return (
    <Card>
      <CardHeader>
        <CardTitle>Generating now ({jobs.length})</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {jobs.map((job) => (
          <div key={job.id} className="flex items-center gap-3 text-sm">
            <LoaderCircle className="size-4 shrink-0 animate-spin text-[var(--color-accent)]" aria-hidden />
            <span className="min-w-0 flex-1 truncate">
              {job.userPrompt ?? 'Curated wallpaper'}
              {job.mood && <span className="text-[var(--color-text-tertiary)]"> · {job.mood}</span>}
            </span>
            <span className="shrink-0 tabular-nums text-[var(--color-text-secondary)]">
              {formatElapsed(job.createdAt, now)}
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

function GenerateCuratedDialog({
  categories,
  styles,
  moods,
  pending,
  onSubmit,
}: {
  categories: Category[]
  styles: StylePreset[]
  moods: MoodPreset[]
  pending: boolean
  onSubmit: (body: Record<string, unknown>) => void
}) {
  const [open, setOpen] = useState(false)
  const [categoryId, setCategoryId] = useState('')
  const [styleId, setStyleId] = useState('')
  const [mood, setMood] = useState('')
  const [prompt, setPrompt] = useState('')
  const [titleEn, setTitleEn] = useState('')

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button disabled={pending}>
          <Sparkles className="size-4" />
          Generate curated
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generate a curated wallpaper</DialogTitle>
          <DialogDescription>
            Generated once and shown to everyone, at a resolution that suits all devices. This costs a real
            model call and runs synchronously.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <select
            className="h-9 w-full rounded-lg border bg-[var(--color-surface)] px-3 text-sm"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">No category</option>
            {categories.filter((c) => c.active).map((c) => (
              <option key={c.id} value={c.id}>{c.nameEn}</option>
            ))}
          </select>

          <select
            className="h-9 w-full rounded-lg border bg-[var(--color-surface)] px-3 text-sm"
            value={styleId}
            onChange={(e) => setStyleId(e.target.value)}
          >
            <option value="">No style</option>
            {styles.filter((s) => s.active).map((s) => (
              <option key={s.id} value={s.id}>{s.nameEn}</option>
            ))}
          </select>

          <select
            className="h-9 w-full rounded-lg border bg-[var(--color-surface)] px-3 text-sm"
            value={mood}
            onChange={(e) => setMood(e.target.value)}
          >
            <option value="">No mood</option>
            {moods.filter((m) => m.active).map((m) => (
              <option key={m.id} value={m.slug}>{m.nameEn}</option>
            ))}
          </select>
          <Textarea
            placeholder="Extra description (optional)"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
          <Input
            placeholder="Title shown in Discover (English)"
            value={titleEn}
            onChange={(e) => setTitleEn(e.target.value)}
          />
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            disabled={pending}
            onClick={() => {
              onSubmit({
                categoryId: categoryId || null,
                styleId: styleId || null,
                mood: mood || null,
                prompt: prompt || null,
                titleEn: titleEn || null,
              })
              setOpen(false)
            }}
          >
            Generate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
